import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { WorkspaceContextService } from '../services/workspace-context.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private readonly workspaceContext: WorkspaceContextService) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Only intercept requests going to the API
    if (!req.url.includes('/api/')) {
      return next.handle(req);
    }

    let headers = req.headers;

    const token = this.resolveAccessToken(
      localStorage.getItem('brickhub_customer_auth') ||
        this.getCookie('brickhub_customer_auth'),
    );
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    if (!headers.has('workspace-id')) {
      const workspaceId = this.resolveWorkspaceIdForRequest(req.url);
      if (workspaceId) {
        headers = headers.set('workspace-id', workspaceId);
      }
    }

    const authReq = req.clone({ headers });
    return next.handle(authReq);
  }

  /** JWTs and ids may be stored raw or JSON-stringified from the landing page. */
  private resolveAccessToken(raw: string | null): string | null {
    const value = this.resolveStoredValue(raw);
    if (!value) {
      return null;
    }
    return value.split('.').length === 3 ? value : null;
  }

  private resolveStoredValue(raw: string | null): string | null {
    if (!raw) {
      return null;
    }
    const trimmed = raw.trim();
    if (!trimmed) {
      return null;
    }
    if (
      (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
      trimmed.startsWith('{')
    ) {
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        if (typeof parsed === 'string' && parsed.trim()) {
          return parsed.trim();
        }
      } catch {
        // fall through to raw value
      }
    }
    return trimmed;
  }

  private resolveWorkspaceIdForRequest(url: string): string | null {
    const isAggregatedPortalList =
      url.includes('/api/v1/customer/dashboard') ||
      url.includes('/api/v1/customer/shortlist') ||
      url.includes('/api/v1/customer/quotations') ||
      url.includes('/api/v1/customer/site-visits') ||
      url.includes('/api/v1/customer/projects');

    if (isAggregatedPortalList) {
      return null;
    }

    const requiresBookingWorkspace =
      url.includes('/api/v1/bookings') ||
      url.includes('/api/v1/payments') ||
      url.includes('/api/v1/receipts') ||
      url.includes('/api/v1/booking/') ||
      url.includes('/api/v1/customer/bookings') ||
      url.includes('/api/v1/loan-applications');

    if (requiresBookingWorkspace) {
      const active = this.workspaceContext.getActiveWorkspaceId();
      if (active) {
        return active;
      }
    }

    return this.resolveStoredValue(
      localStorage.getItem('workspace_id') || this.getCookie('workspace_id'),
    );
  }

  private getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    const nameLenPlus = name.length + 1;
    return (
      document.cookie
        .split(';')
        .map((c) => c.trim())
        .filter((cookie) => cookie.substring(0, nameLenPlus) === `${name}=`)
        .map((cookie) => decodeURIComponent(cookie.substring(nameLenPlus)))[0] ?? null
    );
  }
}
